
import { gToLb } from "@/utils/familyProfile";
import { GrowthChart } from "./GrowthChart";
import { createChartConfig } from "./chart/ChartConfig";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface WeightChartProps { 
  weightData: any[];
  displayOriginalWeight: number | null;
  useMetricWeight: boolean;
  handleWeightUnitToggle: () => void;
  babyAgeInMonths: number;
}

// Average baby weight data (in grams) by month
// These are approximate values based on WHO growth charts
const averageWeightData = [
  { month: 0, value: 3400 },  // Birth
  { month: 1, value: 4500 },  // 1 month
  { month: 2, value: 5600 },  // 2 months
  { month: 3, value: 6400 },  // 3 months
  { month: 4, value: 7000 },  // 4 months
  { month: 6, value: 8000 },  // 6 months
  { month: 9, value: 9000 },  // 9 months
  { month: 12, value: 10000 }, // 12 months
  { month: 15, value: 10700 }, // 15 months
  { month: 18, value: 11200 }, // 18 months
  { month: 24, value: 12200 }, // 24 months (2 years)
  { month: 30, value: 13100 }, // 30 months
  { month: 36, value: 14000 }  // 36 months (3 years)
];

// Chart configuration with more kid-friendly colors
const chartConfig = createChartConfig(
  "weight",
  "Weight",
  {
    light: "#EC4899", // Pink
    dark: "#F472B6"
  }
);

export function WeightChart({ 
  weightData, 
  displayOriginalWeight, 
  useMetricWeight, 
  handleWeightUnitToggle,
  babyAgeInMonths
}: WeightChartProps) {
  const { language } = useLanguage();
  const weightUnitLabel = useMetricWeight ? "g" : "lb";
  
  // Function to get average weight for current age in months
  const getAverageWeightForAge = (ageMonths: number) => {
    // Find the closest data point
    let closestPoint = averageWeightData[0];
    
    for (const point of averageWeightData) {
      if (Math.abs(point.month - ageMonths) < Math.abs(closestPoint.month - ageMonths)) {
        closestPoint = point;
      }
    }
    
    return useMetricWeight ? closestPoint.value : gToLb(closestPoint.value);
  };

  // Convert average data to current unit
  const convertedAverageData = averageWeightData.map(point => ({
    month: point.month,
    value: useMetricWeight ? point.value : gToLb(point.value)
  }));

  return (
    <GrowthChart
      title={t('statistics.weightGrowth', language)}
      description={t('statistics.weightProgress', language)}
      chartData={weightData}
      displayOriginalValue={displayOriginalWeight}
      useMetricUnit={useMetricWeight}
      handleUnitToggle={handleWeightUnitToggle}
      unitLabel={weightUnitLabel}
      averageData={convertedAverageData}
      getAverageValueForAge={getAverageWeightForAge}
      valueKey="displayWeight"
      chartConfig={chartConfig}
      babyAgeInMonths={babyAgeInMonths}
      icon={<div className="p-2 bg-pink-100 rounded-full inline-block"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-500"><path d="M16 16v1a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-1"/><rect x="4" y="8" width="16" height="8" rx="1"/><path d="M4 13h16"/><path d="M8 13v1"/><path d="M16 13v1"/></svg></div>}
    />
  );
}
